import { Stack } from "@mantine/core"
import { InfoBox, SectionHeader } from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { ProductSheetLanguages } from "@tfg/types/constants/language.constants"
import { LanguagePermissionLevelEnum } from "@tfg/types/endpoints/subscriptionPlan"

import { queryClient } from "~/backend/queryClient"
import { useUpdateActiveLanguages } from "~/gen/api"
import { useActiveLanguages } from "~/pages/Brand/CommunicationStudio/DesignStudio/hooks/useActiveLanguages"
import { LanguageMultiSelect } from "~/pages/Brand/CommunicationStudio/Languages/components/LanguageMultiSelect"
import { useOrganizationSubscriptionPlanPermissions } from "~/pages/Brand/CommunicationStudio/contexts/organizationSubscriptionPlanPermissions/useOrganizationSubscriptionPlanPermissions"
import { useProductSheetGroup } from "~/pages/Brand/CommunicationStudio/contexts/productSheetGroup/useProductSheetGroup"
import { useLanguageNames } from "~/utils/languages"

interface ActiveLanguagesProps {
  contentWidth: string
}

export const ActiveLanguages = ({ contentWidth }: ActiveLanguagesProps) => {
  const { t } = useTranslation("communicationStudio")

  const { selectedProductSheetGroup } = useProductSheetGroup()
  const productSheetGroupId = selectedProductSheetGroup?.id

  const [selected, setSelected] = useState<ProductSheetLanguages[]>([
    ProductSheetLanguages.EN,
  ])
  const [maxLanguagesSelectedReached, setMaxLanguagesSelectedReached] =
    useState(false)

  const { permissions } = useOrganizationSubscriptionPlanPermissions()
  const languagesPermissions = permissions?.languages

  useEffect(() => {
    if (languagesPermissions?.level === LanguagePermissionLevelEnum.READONLY) {
      setMaxLanguagesSelectedReached(true)
    }
    if (languagesPermissions?.level === LanguagePermissionLevelEnum.EDIT_ALL) {
      setMaxLanguagesSelectedReached(false)
    }
    if (languagesPermissions?.level === LanguagePermissionLevelEnum.EDIT_2) {
      if (selected.length >= 2) {
        setMaxLanguagesSelectedReached(true)
        return
      }
      setMaxLanguagesSelectedReached(false)
    }
    if (languagesPermissions?.level === LanguagePermissionLevelEnum.EDIT_3) {
      if (selected.length >= 3) {
        setMaxLanguagesSelectedReached(true)
        return
      }
      setMaxLanguagesSelectedReached(false)
    }
  }, [languagesPermissions?.level, selected.length])

  const activeLanguages = useActiveLanguages(productSheetGroupId)

  const listAllActiveLanguages = activeLanguages
    ? (Object.keys(activeLanguages) as ProductSheetLanguages[])
    : []

  const { getLanguageName } = useLanguageNames()

  const languageItems = listAllActiveLanguages.map(
    (locale: ProductSheetLanguages) => ({
      label:
        locale === ProductSheetLanguages.EN
          ? `${getLanguageName(locale)} (default)`
          : getLanguageName(locale),
      value: locale,
      deletable: locale !== ProductSheetLanguages.EN,
    })
  )

  useEffect(() => {
    if (activeLanguages) {
      const listEnabledActiveLanguages = Object.entries(activeLanguages)
        .filter(([_, value]) => value)
        .map(([key]) => key) as ProductSheetLanguages[]

      setSelected(listEnabledActiveLanguages)
    }
  }, [activeLanguages])

  const { mutate: updateActiveLanguages } = useUpdateActiveLanguages({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["activeLanguages", productSheetGroupId],
        })
      },
    },
  })

  const handleChange = (selectedLocales: ProductSheetLanguages[]) => {
    setSelected(selectedLocales)
    updateActiveLanguages({
      productSheetGroupId: productSheetGroupId ?? "",
      data: {
        activeLanguages: {
          [ProductSheetLanguages.EN]: selectedLocales.includes(
            ProductSheetLanguages.EN
          ),
          [ProductSheetLanguages.FR]: selectedLocales.includes(
            ProductSheetLanguages.FR
          ),
          [ProductSheetLanguages.DE]: selectedLocales.includes(
            ProductSheetLanguages.DE
          ),
          [ProductSheetLanguages.IT]: selectedLocales.includes(
            ProductSheetLanguages.IT
          ),
          [ProductSheetLanguages.ES]: selectedLocales.includes(
            ProductSheetLanguages.ES
          ),
          [ProductSheetLanguages.NL]: selectedLocales.includes(
            ProductSheetLanguages.NL
          ),
          [ProductSheetLanguages.AR]: selectedLocales.includes(
            ProductSheetLanguages.AR
          ),
          [ProductSheetLanguages.KO]: selectedLocales.includes(
            ProductSheetLanguages.KO
          ),
          [ProductSheetLanguages.JA]: selectedLocales.includes(
            ProductSheetLanguages.JA
          ),
          [ProductSheetLanguages.BE]: selectedLocales.includes(
            ProductSheetLanguages.BE
          ),
          [ProductSheetLanguages.RO]: selectedLocales.includes(
            ProductSheetLanguages.RO
          ),
          [ProductSheetLanguages.LT]: selectedLocales.includes(
            ProductSheetLanguages.LT
          ),
          [ProductSheetLanguages.ZH]: selectedLocales.includes(
            ProductSheetLanguages.ZH
          ),
        },
      },
    })
  }

  return (
    <Stack gap="xs">
      <SectionHeader
        title={t("tabs.languages.activeLanguages.title")}
        subtitle={t("tabs.languages.activeLanguages.subtitle")}
        style={{ width: contentWidth }}
      />
      <LanguageMultiSelect
        languageItems={languageItems}
        selectedLocales={selected}
        onChange={handleChange}
        width={contentWidth}
        isSelectButtonDisabled={maxLanguagesSelectedReached}
        limitReached={maxLanguagesSelectedReached}
      />
      {maxLanguagesSelectedReached && (
        <InfoBox
          title={t(
            "tabs.languages.activeLanguages.maxLanguagesSelectedReached.infoBox.title"
          )}
          message={t(
            "tabs.languages.activeLanguages.maxLanguagesSelectedReached.infoBox.message"
          )}
          variant="warning"
          width={contentWidth}
        />
      )}
    </Stack>
  )
}
